import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';

interface ImportLeadsDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const sampleCsvContent = `Company,Industry,Contact,Email,City,Postcode,Phone,Address 1,Address 2,Address 3
Acme Corp,Technology,John Smith,john.smith@acmecorp.com,London,EC1A 1BB,01234567890,123 Business St,Suite 101,
Tech Solutions Ltd,Software,Jane Doe,jane.doe@techsolutions.com,Manchester,M1 1AE,07123456789,456 Innovation Ave,,`;

const ImportLeadsDialog: React.FC<ImportLeadsDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];
      if (selectedFile.type === 'text/csv' || selectedFile.type === 'application/vnd.ms-excel') {
        setFile(selectedFile);
        setError(null);
      } else {
        setError('Please select a valid CSV file');
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Get the authentication token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch('/api/leads/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to import contacts');
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during import');
    } finally {
      setUploading(false);
    }
  };

  const theme = useTheme();

  const handleDownloadSample = useCallback(() => {
    const blob = new Blob([sampleCsvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_leads.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, []);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
          backdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        pb: 2,
        color: 'text.primary'
      }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'medium' }}>
          Import Contacts
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          {/* Instructions Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon fontSize="small" />
              Instructions
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Prepare your CSV file with the required columns"
                  secondary="Company and Contact are mandatory fields"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Ensure data is properly formatted"
                  secondary="Phone numbers should start with 0 (e.g., 01234567890)"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Review the data before uploading"
                  secondary="The system will validate all entries before importing"
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Upload Section */}
          <Box sx={{ 
            mb: 3,
            mx: 'auto',
            width: '100%',
            maxWidth: 500
          }}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                border: '2px dashed',
                borderColor: file ? 'success.main' : 'rgba(255, 255, 255, 0.2)',
                borderRadius: '0.75rem',
                bgcolor: file ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(8px)',
                transition: theme.transitions.create(['border-color', 'background-color']),
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(59, 130, 246, 0.08)',
                },
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 200
              }}
              component="label"
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 2,
                textAlign: 'center',
                width: '100%'
              }}>
                <CloudUploadIcon sx={{ 
                  fontSize: 56, 
                  color: file ? 'success.main' : 'primary.main',
                  transition: theme.transitions.create('color'),
                }} />
                <Typography variant="h6" align="center" color={file ? 'success.main' : 'text.primary'}>
                  {file ? file.name : 'Drop your CSV file here or click to browse'}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  Maximum file size: 5MB
                </Typography>
              </Box>
            </Paper>
          </Box>

          {/* Sample File Section */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleDownloadSample}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Download sample CSV template
            </Button>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 3,
                borderRadius: '0.75rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              }}
              variant="filled"
            >
              {error}
            </Alert>
          )}

          {uploading && (
            <Box sx={{ mt: 3 }}>
              <LinearProgress 
                sx={{ 
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: '#3b82f6'
                  }
                }} 
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 1,
                  textAlign: 'center',
                  color: 'primary.main',
                  fontWeight: 'medium'
                }}
              >
                Processing your file... Please wait
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        p: 2,
        gap: 1,
        display: 'flex',
        justifyContent: 'flex-end'
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            minWidth: 100
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          disabled={!file || uploading}
          variant="contained"
          color="primary"
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            minWidth: 100
          }}
          startIcon={<CloudUploadIcon />}
        >
          {uploading ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportLeadsDialog;
